import React, { useCallback, useMemo, useState } from "react";
import { Box, Paper, useMediaQuery, useTheme } from "@mui/material";
import { DateTime } from "luxon";
import type { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import type {
  Dashboard2DayBookingSummary,
  Dashboard2WeekDay,
  DayWorkStatusKind
} from "../types/dashboard2-work-status.types";
import {
  dashboard2WeekPlanningGridFillSx,
  dashboard2WeekPlanningHeroFillSx,
  dashboard2WeekNavFillSx,
  dashboard2WeekPlanningMobileDaySx,
  dashboard2WeekPlanningShellSx
} from "../dashboard2-status.styles";
import { countPlannedWorkdays } from "../utils/dashboard2-schedule.utils";
import type { WeekDayCardActionKind } from "../utils/dashboard2-week-day-card.utils";
import { Dashboard2StatusSheet } from "./Dashboard2StatusSheet";
import { Dashboard2WeekNavigation } from "./Dashboard2WeekNavigation";
import { Dashboard2WeekPlanIntro } from "./Dashboard2WeekPlanIntro";
import { Dashboard2WeekStatusCell } from "./Dashboard2WeekStatusCell";
import {
  Dashboard2WeekPlanIntroLoading,
  Dashboard2WeekPlanningDaysLoading
} from "./Dashboard2SchedulesLoadingSkeleton";

type SheetState = {
  open: boolean;
  day: Dashboard2WeekDay | null;
};

type Dashboard2WeekPlanningGridProps = {
  weekAnchor: DateTime;
  weekDays: Dashboard2WeekDay[];
  onPreviousWeek: () => void;
  onNextWeek: () => void;
  onWeekSelect: (week: DateTime) => void;
  onSelectStatus?: (kind: DayWorkStatusKind, day: Dashboard2WeekDay) => void;
  onPrimaryScheduleClick?: (schedule: BookingScheduleInterface) => void;
  onWeekDayAction?: (actionKind: WeekDayCardActionKind, day: Dashboard2WeekDay) => void;
  onBookingClick?: (booking: Dashboard2DayBookingSummary) => void;
  mobileWorkingEnabled?: boolean;
  isLoading?: boolean;
  statusActionsDisabled?: boolean;
};

export const Dashboard2WeekPlanningGrid: React.FC<Dashboard2WeekPlanningGridProps> = ({
  weekAnchor,
  weekDays,
  onPreviousWeek,
  onNextWeek,
  onWeekSelect,
  onSelectStatus,
  onPrimaryScheduleClick,
  onWeekDayAction,
  onBookingClick,
  mobileWorkingEnabled = true,
  isLoading = false,
  statusActionsDisabled = false
}) => {
  const theme = useTheme();
  const isCompactViewport = useMediaQuery(theme.breakpoints.down("lg"));

  const [sheet, setSheet] = useState<SheetState>({ open: false, day: null });

  const plannedWorkdays = useMemo(
    () => countPlannedWorkdays(weekDays.map(day => day.userStatus)),
    [weekDays]
  );

  const handleUserStatusClick = useCallback(
    (day: Dashboard2WeekDay) => {
      if (statusActionsDisabled) {
        return;
      }

      setSheet({ open: true, day });
    },
    [statusActionsDisabled]
  );

  const handleCloseSheet = useCallback(() => {
    setSheet({ open: false, day: null });
  }, []);

  const handleSelectStatus = useCallback(
    (kind: DayWorkStatusKind) => {
      if (!sheet.day || kind === "public_holiday" || kind === "other_bookings") {
        return;
      }

      onSelectStatus?.(kind, sheet.day);
      handleCloseSheet();
    },
    [handleCloseSheet, onSelectStatus, sheet.day]
  );

  const handleStatusSheetPrimaryScheduleClick = useCallback(
    (schedule: BookingScheduleInterface) => {
      onPrimaryScheduleClick?.(schedule);
      handleCloseSheet();
    },
    [handleCloseSheet, onPrimaryScheduleClick]
  );

  const handlePrimaryScheduleClick = useCallback(
    (day: Dashboard2WeekDay) => {
      if (day.primarySchedule) {
        onPrimaryScheduleClick?.(day.primarySchedule);
      }
    },
    [onPrimaryScheduleClick]
  );

  const handleWeekDayAction = useCallback(
    (actionKind: WeekDayCardActionKind, day: Dashboard2WeekDay) => {
      if (actionKind === "plan_holiday") {
        handleUserStatusClick(day);
        return;
      }

      onWeekDayAction?.(actionKind, day);
    },
    [handleUserStatusClick, onWeekDayAction]
  );

  return (
    <>
      <Box sx={dashboard2WeekPlanningShellSx} data-testid="dashboard2-week-planning-shell">
        <Paper
          variant="outlined"
          sx={dashboard2WeekPlanningHeroFillSx(theme)}
          data-testid="dashboard2-week-planning-grid"
        >
          {isLoading ? (
            <Dashboard2WeekPlanIntroLoading />
          ) : (
            <Dashboard2WeekPlanIntro
              planned={plannedWorkdays.planned}
              total={plannedWorkdays.total}
            />
          )}
          <Box sx={dashboard2WeekNavFillSx}>
            <Dashboard2WeekNavigation
              weekAnchor={weekAnchor}
              onPreviousWeek={onPreviousWeek}
              onNextWeek={onNextWeek}
              onWeekSelect={onWeekSelect}
            />
          </Box>

          {isLoading ? (
            <Dashboard2WeekPlanningDaysLoading
              isCompactViewport={isCompactViewport}
              workDayCount={weekDays.length}
            />
          ) : (
            <Box sx={dashboard2WeekPlanningGridFillSx(isCompactViewport, weekDays.length)}>
              {weekDays.map(day =>
                isCompactViewport ? (
                  <Box key={day.dayKey} sx={dashboard2WeekPlanningMobileDaySx}>
                    <Dashboard2WeekStatusCell
                      day={day}
                      onUserStatusClick={handleUserStatusClick}
                      onPrimaryScheduleClick={handlePrimaryScheduleClick}
                      onWeekDayAction={handleWeekDayAction}
                      onBookingClick={onBookingClick}
                      openStatusSheetDayKey={sheet.open ? sheet.day?.dayKey : undefined}
                    />
                  </Box>
                ) : (
                  <Dashboard2WeekStatusCell
                    key={day.dayKey}
                    day={day}
                    onUserStatusClick={handleUserStatusClick}
                    onPrimaryScheduleClick={handlePrimaryScheduleClick}
                    onWeekDayAction={handleWeekDayAction}
                    onBookingClick={onBookingClick}
                    openStatusSheetDayKey={sheet.open ? sheet.day?.dayKey : undefined}
                  />
                )
              )}
            </Box>
          )}
        </Paper>
      </Box>

      {sheet.day && (
        <Dashboard2StatusSheet
          open={sheet.open}
          dayLabel={sheet.day.label}
          currentStatus={sheet.day.userStatus}
          onClose={handleCloseSheet}
          onSelectStatus={handleSelectStatus}
          onBookingClick={onBookingClick}
          primarySchedule={sheet.day.primarySchedule}
          onPrimaryScheduleClick={handleStatusSheetPrimaryScheduleClick}
          mobileWorkingEnabled={mobileWorkingEnabled}
        />
      )}
    </>
  );
};
